// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2025-2026 Daragh Farrell

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import i18next from 'i18next';
import api from '../api/client';
import { useSession } from '../context/SessionContext';
import { useAuth } from '../auth/AuthProvider';
import { setAccessToken } from '../auth/auth-store';
import { consumeLogoutReason, type LogoutReason } from '../auth/logout-reason';
import { isNativePlatform } from '../utils/platform';
import { useBiometricAuth } from '../hooks/useBiometricAuth';
import TotpLoginChallenge from '../components/TotpLoginChallenge';
import Logo from '../components/Logo';
import { formatMemberDisplayName } from '../components/MemberName';
import BiometricPrompt from '../components/auth/BiometricPrompt';
import BiometricSetup from '../components/auth/BiometricSetup';
import ClubSelector from '../components/ClubSelector';

// A100: Roles that should route to /dashboard instead of /portal
const DASHBOARD_ROLES = ['admin', 'treasurer', 'coach', 'instructor', 'organiser', 'dso'];

// A88: Multi-club entry from backend
type ClubEntry = {
  club_id: string;
  club_name: string;
  member_id: string;
  roles?: string[];
};

export default function Login() {
  const { session, setSession } = useSession();
  const { setAuthenticated } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [retryAfter, setRetryAfter] = useState<number>(0);
  const [requiresTotp, setRequiresTotp] = useState(false);
  const [totpToken, setTotpToken] = useState('');
  const [totpError, setTotpError] = useState('');
  const [totpLoading, setTotpLoading] = useState(false);
  const [pendingMember, setPendingMember] = useState<{
    id: string; club_id: string; first_name?: string; last_name?: string; email: string; roles?: string[];
  } | null>(null);
  const [showBiometricOffer, setShowBiometricOffer] = useState(false);
  const [biometricAttempting, setBiometricAttempting] = useState(false);
  const biometricAttemptedRef = useRef(false);
  // A88: Multi-club state
  const [multiClubData, setMultiClubData] = useState<ClubEntry[] | null>(null);
  const [rememberClub, setRememberClub] = useState(false);
  // N37: No-memberships state
  // W74: why the last session ended, when the server said. Informational, not an
  // error — an inactivity sign-out is expected behaviour, not a failed login.
  const [logoutReason, setLogoutReason] = useState<LogoutReason | null>(null);
  const [noMemberships, setNoMemberships] = useState(false);
  const [userToken, setUserToken] = useState('');
  const [joinClubId, setJoinClubId] = useState('');

  const navigate = useNavigate();

  const {
    biometricAvailable,
    biometricType,
    biometricEnabled,
    enableBiometric,
    attemptBiometricLogin,
  } = useBiometricAuth();

  const errorId = error ? 'login-error' : undefined;
  const biometricLabel = biometricType === 'face' ? 'Face ID' : biometricType === 'fingerprint' ? 'Touch ID' : 'biometrics';

  type MemberData = { id: string; club_id: string; first_name?: string; last_name?: string; email: string; roles?: string[]; locale?: string };

  const buildSession = useCallback((member: MemberData, clubName?: string) => ({
    // The session username is the logged-in user's own name. Children cannot
    // authenticate (W66/ADR-001), so this never sees a child member; pass
    // 'admin' so the helper returns the full name for self-view consistency.
    username: formatMemberDisplayName({ first_name: member.first_name ?? '', last_name: member.last_name ?? '' }, 'admin') || member.email || session?.username || 'member',
    role: member.roles?.[0] || session?.role || 'member',
    roles: member.roles || [],
    club: { id: member.club_id, name: clubName || session?.club.name || 'Club' },
    member_id: member.id,
  }), [session]);

  const completeLogin = useCallback((data: { access_token: string; expires_in?: number; member: MemberData; club_name?: string }) => {
    const { access_token: accessToken, expires_in: expiresIn, member, club_name: clubName } = data;
    setAccessToken(accessToken, expiresIn || 900);
    localStorage.removeItem('clubview_club_token');
    setSession(buildSession(member, clubName));
    if (member.locale) i18next.changeLanguage(member.locale);
    setAuthenticated(true);
    navigate(member.roles?.some((r: string) => DASHBOARD_ROLES.includes(r)) ? '/dashboard' : '/portal');
  }, [buildSession, setSession, setAuthenticated, navigate]);

  // Attempt biometric login on mount (native only, if enabled)
  useEffect(() => {
    if (!isNativePlatform() || !biometricEnabled || biometricAttemptedRef.current) return;
    biometricAttemptedRef.current = true;

    const tryBiometric = async () => {
      setBiometricAttempting(true);
      try {
        const deviceToken = await attemptBiometricLogin();
        if (!deviceToken) { setBiometricAttempting(false); return; }

        const res = await api.post('/auth/biometric-login', {
          device_token: deviceToken,
        }, { withCredentials: true });

        if (res?.data?.access_token && res?.data?.member) {
          completeLogin(res.data);
          return;
        }
      } catch {
        // Biometric login failed, fall back to password
      }
      setBiometricAttempting(false);
    };

    tryBiometric();
  }, [biometricEnabled, attemptBiometricLogin, completeLogin]);

  // Read the sign-out reason once and consume it, so returning to /login later
  // does not repeat a message about a session that ended long ago.
  useEffect(() => {
    setLogoutReason(consumeLogoutReason());
  }, []);

  // Countdown timer for retry cooldown
  useEffect(() => {
    if (retryAfter <= 0) return;
    const timer = setInterval(() => {
      setRetryAfter((prev) => {
        if (prev <= 1) { setError(''); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [retryAfter]);

  const formatRetryTime = useCallback((seconds: number): string => {
    if (seconds >= 60) {
      const mins = Math.ceil(seconds / 60);
      return `${mins} minute${mins > 1 ? 's' : ''}`;
    }
    return `${seconds} second${seconds !== 1 ? 's' : ''}`;
  }, []);

  // A88: Get the localStorage key for preferred club (keyed by email)
  const getPreferredClubKey = (emailAddr: string) =>
    `clubview_preferred_club_${btoa(emailAddr.trim())}`;

  // A88: Handle club selection from the picker
  const handleClubSelect = async (clubId: string) => {
    setError('');
    setLoading(true);
    try {
      // Save preference if "remember" is checked
      if (rememberClub) {
        localStorage.setItem(getPreferredClubKey(email), clubId);
      }

      const loginResponse = await api.post('/auth/member/login', {
        email: email.trim(), password: password.trim(), club_id: clubId,
      }, { withCredentials: true });

      const accessToken = loginResponse?.data?.access_token as string | undefined;
      const expiresIn = loginResponse?.data?.expires_in as number | undefined;
      const multiClubName = loginResponse?.data?.club_name as string | undefined;
      const member = loginResponse?.data?.member as
        | { id: string; club_id: string; first_name?: string; last_name?: string; email: string; roles?: string[]; locale?: string }
        | undefined;

      if (!accessToken || !member?.id || !member?.club_id) { throw new Error('Invalid login response'); }

      completeLogin({ access_token: accessToken, expires_in: expiresIn, member, club_name: multiClubName });
    } catch (err: unknown) {
      let errorMessage = 'Unable to connect. Please try again.';
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string }; status?: number } };
        if (axiosError.response?.status === 401) errorMessage = 'Invalid email or password';
        else if (axiosError.response?.data?.message) errorMessage = axiosError.response.data.message;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) { setError('Please enter email and password'); return; }
    setError('');
    setLoading(true);
    try {
      // A88: Check for remembered club preference
      const preferredClubId = localStorage.getItem(getPreferredClubKey(email));
      const payload: { email: string; password: string; club_id?: string } = {
        email: email.trim(), password: password.trim(),
      };
      if (preferredClubId) {
        payload.club_id = preferredClubId;
      }

      const loginResponse = await api.post('/auth/member/login', payload, { withCredentials: true });

      // A88: Multi-club detection
      if (loginResponse?.data?.multi_club && loginResponse?.data?.clubs) {
        // If we had a preferred club but got multi_club back, the preference is stale
        if (preferredClubId) {
          localStorage.removeItem(getPreferredClubKey(email));
        }
        setMultiClubData(loginResponse.data.clubs as ClubEntry[]);
        return;
      }

      if (loginResponse?.data?.requires_totp) {
        setTotpToken(loginResponse.data.totp_token || '');
        setPendingMember(loginResponse.data.member || null);
        setRequiresTotp(true);
        return;
      }

      const accessToken = loginResponse?.data?.access_token as string | undefined;
      const expiresIn = loginResponse?.data?.expires_in as number | undefined;
      const clubName = loginResponse?.data?.club_name as string | undefined;
      const member = loginResponse?.data?.member as
        | { id: string; club_id: string; first_name?: string; last_name?: string; email: string; roles?: string[]; locale?: string }
        | undefined;

      if (!accessToken || !member?.id || !member?.club_id) { throw new Error('Invalid login response'); }

      if (isNativePlatform() && biometricAvailable && !biometricEnabled) {
        setAccessToken(accessToken, expiresIn || 900);
        localStorage.removeItem('clubview_club_token');
        setSession(buildSession(member, clubName));
        if (member.locale) i18next.changeLanguage(member.locale);
        setAuthenticated(true);
        setShowBiometricOffer(true);
        return;
      }

      completeLogin({ access_token: accessToken, expires_in: expiresIn, member, club_name: clubName });
    } catch (err: unknown) {
      let errorMessage = 'Unable to connect. Please try again.';
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string; club_status?: string }; status?: number; headers?: { 'retry-after'?: string } } };
        const status = axiosError.response?.status;
        const clubStatus = axiosError.response?.data?.club_status;
        if (status === 400) errorMessage = 'Please enter email and password';
        else if (status === 401) {
          // N37: Try user-level login — user may exist but have no club memberships
          try {
            const userRes = await api.post('/users/login', {
              email: email.trim(), password: password.trim(),
            });
            if (userRes?.data?.token) {
              // User exists — check if they have any clubs
              const clubsRes = await api.get('/users/me/clubs', {
                headers: { Authorization: `Bearer ${userRes.data.token}` },
              });
              const clubs = clubsRes?.data as Array<{ club_id: string }> | undefined;
              if (!clubs || clubs.length === 0) {
                // No memberships — show join-a-club prompt
                setUserToken(userRes.data.token);
                setNoMemberships(true);
                setLoading(false);
                return;
              }
              // Has clubs but member login still failed — memberships may be pending
              setError('Your club memberships are pending approval.');
              setLoading(false);
              return;
            }
          } catch {
            // User login also failed — truly invalid credentials
          }
          errorMessage = 'Invalid email or password';
        } else if (status === 403 && clubStatus === 'pending') {
          navigate('/club-pending-approval');
          return;
        } else if (status === 403 && clubStatus === 'rejected') {
          errorMessage = axiosError.response?.data?.message || 'Your club registration was not approved.';
        } else if (status === 403 && clubStatus === 'suspended') {
          errorMessage = axiosError.response?.data?.message || 'Your club has been suspended. Please contact support.';
        } else if (status === 403) errorMessage = axiosError.response?.data?.message || 'Account is not active.';
        else if (status === 429) {
          const retrySeconds = axiosError.response?.headers?.['retry-after'] ? parseInt(axiosError.response.headers['retry-after'], 10) || 30 : 30;
          setRetryAfter(retrySeconds);
          errorMessage = `Too many login attempts. Please wait ${formatRetryTime(retrySeconds)} before trying again.`;
        } else if (axiosError.response?.data?.message) errorMessage = axiosError.response.data.message;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleTotpSubmit = async (code: string, isRecovery: boolean) => {
    setTotpError('');
    setTotpLoading(true);
    try {
      const res = await api.post('/auth/totp/validate', {
        totp_token: totpToken, code, is_recovery: isRecovery,
      }, { withCredentials: true });

      const accessToken = res?.data?.access_token as string | undefined;
      const expiresIn = res?.data?.expires_in as number | undefined;
      const totpClubName = res?.data?.club_name as string | undefined;
      const member = res?.data?.member || pendingMember;

      if (!accessToken || !member?.id || !member?.club_id) { throw new Error('Invalid TOTP response'); }

      setAccessToken(accessToken, expiresIn || 900);
      localStorage.removeItem('clubview_club_token');
      setSession(buildSession(member, totpClubName));
      if ((member as { locale?: string })?.locale) i18next.changeLanguage((member as { locale?: string }).locale!);
      setAuthenticated(true);
      navigate(member.roles?.some((r: string) => DASHBOARD_ROLES.includes(r)) ? '/dashboard' : '/portal');
    } catch {
      setTotpError('Invalid code. Please try again.');
    } finally {
      setTotpLoading(false);
    }
  };

  // N37: Join club from login (no-memberships state)
  const handleJoinFromLogin = async () => {
    if (!joinClubId) return;
    setError('');
    setLoading(true);
    try {
      await api.post('/users/me/join-club', { club_id: joinClubId }, {
        headers: { Authorization: `Bearer ${userToken}` },
      });
      navigate('/registration-pending', { state: { email } });
    } catch (err: unknown) {
      let errorMessage = 'Unable to join club. Please try again.';
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string }; status?: number } };
        if (axiosError.response?.status === 409) errorMessage = 'You are already a member of this club.';
        else if (axiosError.response?.data?.message) errorMessage = axiosError.response.data.message;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // --- Biometric screens (extracted components) ---

  if (showBiometricOffer) {
    const handleEnableBiometric = async () => {
      try {
        const deviceId = `${navigator.userAgent.slice(0, 50)}-${Date.now()}`;
        const res = await api.post('/auth/biometric-token', { device_id: deviceId });
        if (res?.data?.device_token && res?.data?.expires_in) {
          await enableBiometric(res.data.device_token, res.data.expires_in);
        }
      } catch { /* Non-critical */ }
      navigate(session?.roles?.some((r: string) => DASHBOARD_ROLES.includes(r)) ? '/dashboard' : '/portal');
    };

    const handleSkipBiometric = () => {
      navigate(session?.roles?.some((r: string) => DASHBOARD_ROLES.includes(r)) ? '/dashboard' : '/portal');
    };

    return <BiometricSetup biometricLabel={biometricLabel} onEnable={handleEnableBiometric} onSkip={handleSkipBiometric} />;
  }

  if (biometricAttempting) {
    return <BiometricPrompt biometricLabel={biometricLabel} onCancel={() => setBiometricAttempting(false)} />;
  }

  // N37: No-memberships — user exists but has no clubs
  if (noMemberships) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-brand-50 via-white to-white px-4 py-10">
        <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-md">
          <div className="mb-4 flex justify-center">
            <Logo variant="full" size="lg" />
          </div>
          <h2 className="mb-2 text-center text-xl font-semibold text-brand-700">
            Welcome back
          </h2>
          <p className="mb-6 text-center text-sm text-slate-600">
            You don't belong to any clubs yet. Join one to get started.
          </p>
          {error && (
            <p role="alert" className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </p>
          )}
          <div className="space-y-4">
            <ClubSelector
              value={joinClubId}
              onChange={(id) => setJoinClubId(id)}
            />
            <button
              type="button"
              data-testid="login-join-club"
              onClick={handleJoinFromLogin}
              disabled={loading || !joinClubId}
              className="w-full rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-200 disabled:opacity-50"
            >
              {loading ? 'Joining...' : 'Join Club'}
            </button>
            <button
              type="button"
              data-testid="login-no-clubs-back"
              onClick={() => { setNoMemberships(false); setError(''); }}
              className="w-full rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-brand-200"
            >
              Back to sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (requiresTotp) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-brand-50 via-white to-white px-4 py-10">
        <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-md">
          <TotpLoginChallenge onSubmit={handleTotpSubmit} error={totpError} loading={totpLoading} />
        </div>
      </div>
    );
  }

  // A88: Club picker step
  if (multiClubData) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-brand-50 via-white to-white px-4 py-10">
        <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-md">
          <div className="mb-4 flex justify-center">
            <Logo variant="full" size="lg" />
          </div>
          <h2 className="mb-2 text-center text-xl font-semibold text-brand-700">
            You're a member of multiple clubs
          </h2>
          <p className="mb-6 text-center text-sm text-slate-600">
            Select which club you'd like to sign into.
          </p>
          {error && (
            <p role="alert" className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </p>
          )}
          <div className={multiClubData.length === 2 ? 'grid grid-cols-2 gap-3' : 'flex flex-col gap-3'}>
            {multiClubData.map((club) => (
              <button
                key={club.club_id}
                data-testid={`club-picker-${club.club_id}`}
                onClick={() => handleClubSelect(club.club_id)}
                disabled={loading}
                className="rounded-lg border border-slate-200 px-4 py-3 text-left transition hover:border-brand-400 hover:bg-brand-50 focus:outline-none focus:ring-2 focus:ring-brand-200 disabled:opacity-50"
              >
                <span className="block text-sm font-semibold text-brand-700">{club.club_name}</span>
                {club.roles && club.roles.length > 0 && (
                  <span className="block text-xs text-slate-500 mt-1">{club.roles.join(', ')}</span>
                )}
              </button>
            ))}
          </div>
          <label className="mt-4 flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              data-testid="remember-club-choice"
              checked={rememberClub}
              onChange={(e) => setRememberClub(e.target.checked)}
              className="rounded border-slate-300 text-brand-600 focus:ring-brand-200"
            />
            Remember my choice
          </label>
          <button
            data-testid="club-picker-back"
            onClick={() => { setMultiClubData(null); setError(''); }}
            className="mt-4 w-full rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-brand-200"
          >
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-brand-50 via-white to-white px-4 py-10">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-md">
        <div className="mb-4 flex justify-center">
          <Logo variant="full" size="lg" />
        </div>
        <h1 className="mb-2 text-center text-2xl font-semibold text-brand-700">Sign in</h1>
        <p className="mb-6 text-center text-sm text-slate-600">
          Sign in with your member email and password.
        </p>
        <form className="space-y-4" onSubmit={handleSubmit} aria-describedby={errorId} noValidate data-testid="login-form">
          <div className="space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="email">Email</label>
              <input id="email" type="email" data-testid="login-email"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
                placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)}
                autoComplete="email" required aria-required="true" />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="password">Password</label>
              <input id="password" type="password" data-testid="login-password"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
                placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password" required aria-required="true" />
            </div>
            <div className="text-right">
              <Link to="/forgot-password" className="text-xs font-medium text-brand-600 hover:text-brand-700">
                Forgot password?
              </Link>
            </div>
          </div>
          <div aria-live="polite">
            {error ? (
              <p id={errorId} role="alert" data-testid="login-error" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </p>
            ) : logoutReason === 'idle_timeout' ? (
              <p role="status" data-testid="logout-reason" className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
                You were signed out because of inactivity. Please sign in again.
              </p>
            ) : (
              <p className="text-xs text-slate-500">Use your email and password to continue.</p>
            )}
          </div>
          <button type="submit" data-testid="login-submit"
            className={`w-full rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition focus:outline-none focus:ring-2 focus:ring-brand-200 ${
              loading || retryAfter > 0 ? 'bg-slate-400 cursor-not-allowed' : 'bg-brand-600 hover:bg-brand-700'
            }`}
            disabled={loading || retryAfter > 0}
          >
            {loading ? 'Signing in...' : retryAfter > 0 ? `Please wait ${formatRetryTime(retryAfter)}` : 'Continue to dashboard'}
          </button>
          <div className="text-center text-sm space-y-2">
            <div>
              <span className="text-slate-600">Don't have an account? </span>
              <Link to="/register" className="text-brand-600 hover:text-brand-700">Sign up</Link>
            </div>
            <div className="border-t border-slate-200 pt-2">
              <span className="text-slate-600">Starting a new club? </span>
              <Link to="/register-club" className="text-brand-600 hover:text-brand-700 font-medium">Start a Club</Link>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
